'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import {
    Align,
    Box,
    Column,
    Columns,
    Icon,
    IconName,
    IconSize,
    Text,
    TextLevels,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { SimulatorPack } from '@app-types/simulator/simulator';

const packList: Array<SimulatorPack> = [
    {
        name: 'ONE_PLAN',
        content: 'Bbox + 1 forfait',
        nbPlan: 1,
        icon: IconName.USER,
        isActive: true,
        isDefault: true,
    },
    {
        name: 'TWO_PLAN',
        content: 'Bbox + 2 forfaits',
        nbPlan: 2,
        icon: IconName.USERS,
        isActive: false,
        isDefault: false,
    },
    {
        name: 'THREE_PLAN',
        content: 'Bbox + 3 forfaits',
        nbPlan: 3,
        icon: IconName.COMMUNITY,
        isActive: false,
        isDefault: false,
    },
    {
        name: 'FOUR_PLAN',
        content: 'Bbox + au moins 4 forfaits',
        nbPlan: 4,
        icon: IconName.PLUS_CIRCLE,
        isActive: false,
        isDefault: false,
    },
];

interface PackProps {
    onSelectedPackChange: (pack: SimulatorPack) => void;
}

export function Pack({ onSelectedPackChange }: Readonly<PackProps>) {
    const [packs, setPacks] = useState(packList);
    const searchParams = useSearchParams();

    const onSelectedPack = (selectedPack: SimulatorPack) => {
        packList.forEach((pack) => {
            pack.isActive = pack.name === selectedPack.name;
        });
        setPacks([...packList]);
        onSelectedPackChange(selectedPack);
    };

    useEffect(() => {
        if (searchParams.has('family-pack')) {
            const selectedPack = packList.find((pack) => pack.nbPlan === Number(searchParams.get('family-pack')));
            if (selectedPack) {
                onSelectedPack(selectedPack);
            }
        }
    }, [searchParams]);

    return (
        <Columns data-cy='pack-component' mobile scrollable gap={6} className='has-scrollbar'>
            {packs.map((pack) => (
                <PackComponent key={pack.name} pack={pack} setSelectedPack={onSelectedPack} />
            ))}
        </Columns>
    );
}

interface PackComponentProps {
    pack: SimulatorPack;
    isSlider?: boolean;
    setSelectedPack: (pack: SimulatorPack) => void;
}

function PackComponent({ pack, setSelectedPack }: Readonly<PackComponentProps>) {
    return (
        <Column mobileSize={9} desktopSize={2} tabletSize={3}>
            <Box
                shadowless
                data-cy={`packbox-${pack.nbPlan}`}
                active={pack.isActive}
                onClick={() => setSelectedPack(pack)}
                fullheight
                className='is-narrow is-justified-center '
            >
                <View flexable className='gap-medium' align={Align.CENTER}>
                    <Icon size={IconSize.MEDIUM} name={pack.icon} />
                    <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                        {pack.content}
                    </Text>
                </View>
            </Box>
        </Column>
    );
}
